import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { AuthShell, FormInput, PrimaryButton, ErrorBanner } from "@/components/auth/AuthShell";

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const next = new URLSearchParams(location.search).get("next") || "/dashboard";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "owner") navigate("/owner");
      else navigate(next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to Studios"
      footer={
        <div className="flex flex-col gap-2">
          <span>
            Don't have an account?{" "}
            <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="link-to-register">
              Create one
            </Link>
          </span>
          <Link to="/forgot-password" className="text-[#A0A0A0] hover:text-[#D4AF37] text-xs" data-testid="link-to-forgot">
            Forgot your password?
          </Link>
        </div>
      }
    >
      <form onSubmit={onSubmit} data-testid="login-form">
        <ErrorBanner message={error} testId="login-error" />
        <FormInput label="Email" type="email" value={email} onChange={setEmail} placeholder="you@business.com" autoComplete="email" required testId="login-email-input" />
        <FormInput label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" autoComplete="current-password" required testId="login-password-input" />
        <PrimaryButton disabled={loading} testId="login-submit-button">
          {loading ? "Signing in…" : "Sign In"}
        </PrimaryButton>
      </form>
      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-[#2A2A2A]" />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A]">or</span>
        <div className="flex-1 h-px bg-[#2A2A2A]" />
      </div>
      <button
        onClick={loginWithGoogle}
        type="button"
        data-testid="login-google-button"
        className="w-full inline-flex items-center justify-center gap-3 border border-[#2A2A2A] hover:border-[#F4F4F0] bg-[#0F0F0F] text-[#F4F4F0] py-3 transition-colors"
      >
        <GoogleIcon /> Continue with Google
      </button>
    </AuthShell>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.6-5.6C34 6.4 29.3 4.5 24 4.5 12.7 4.5 3.5 13.7 3.5 25S12.7 45.5 24 45.5 44.5 36.3 44.5 25c0-1.5-.2-3-.5-4.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 7.9 3l5.6-5.6C34 6.4 29.3 4.5 24 4.5 16.2 4.5 9.4 8.9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.2-7.2 2.2-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.3 41.1 16.1 45.5 24 45.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.3 36.6 44.5 31.2 44.5 25c0-1.5-.2-3-.5-4.5z"/>
    </svg>
  );
}
